import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowBack, Close as CloseIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { User } from 'firebase/auth';
import { type CrewPostLinks, Timestamp } from '@watchlist/shared';
import ResponsiveContainer from '../layouts/ResponsiveContainer';
import { font } from '../../utils/fontHelper';
import {
  createCrewPost,
  deleteCrewPost,
  fetchCrewPostsForFeed,
  updateCrewPostPin,
  MAX_PINNED_CREW_POSTS,
  countActivePinsExcluding,
} from '../../services/crewPostService';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { formatFirebaseDate } from '../../utils/dateUtils';
import { AdminPublishPreviewShell } from './AdminPublishPreviewShell';
import { CrewPostComposePreviewPanel } from './CrewPostComposePreviewPanel';
import ReportedCommentsPanel from './ReportedCommentsPanel';
import ReportedPostsPanel from './ReportedPostsPanel';
import { crewPostDocToFeedItem, timestampOrMillis, WATCHLIST_CREW_UID } from '../../utils/crewFeedUtils';

const TAB_CREW_POSTS = 0;
const TAB_REPORTED_COMMENTS = 1;
const TAB_REPORTED_POSTS = 2;

interface ModeratorConsoleProps {
  user: User;
  onBack: () => void;
}

function parseIdList(raw: string): string[] {
  return raw
    .split(/[\s,;\n]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

const MS_PER_DAY = 86400000;

const PIN_DURATION_DAYS = [1, 3, 7, 14, 30, 60, 90] as const;

type RecentCrewPostRow = {
  id: string;
  title: string;
  authorUid: string;
  createdAt: unknown;
  createdAtMillis?: number;
  pinExpiresAt: unknown;
  pinExpiresAtMillis?: number;
  isPinned: boolean;
};

const ModeratorConsole: React.FC<ModeratorConsoleProps> = ({ user, onBack }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isAdmin } = useAuth();
  const { getUserDisplayName, getUserAvatar, preloadProfiles } = useUserProfileCache();

  const [activeTab, setActiveTab] = useState<number>(TAB_CREW_POSTS);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [watchlistIds, setWatchlistIds] = useState('');
  const [movieIds, setMovieIds] = useState('');
  const [reviewIds, setReviewIds] = useState('');
  const [userIds, setUserIds] = useState('');
  const [personIds, setPersonIds] = useState('');

  const [pinToTop, setPinToTop] = useState(false);
  const [composePinDays, setComposePinDays] = useState(7);
  const [activePinCount, setActivePinCount] = useState(0);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [recallPost, setRecallPost] = useState<{ id: string; title: string; authorUid: string } | null>(null);
  const [pinDialogPost, setPinDialogPost] = useState<{ id: string; title: string; isPinned: boolean } | null>(null);
  const [pinDialogDays, setPinDialogDays] = useState(7);

  const [recent, setRecent] = useState<RecentCrewPostRow[]>([]);

  useEffect(() => {
    preloadProfiles([WATCHLIST_CREW_UID]).catch(() => {});
  }, [preloadProfiles]);

  const crewDisplayName = getUserDisplayName(WATCHLIST_CREW_UID, 'Watchlist Crew') || 'Watchlist Crew';
  const crewAvatar = getUserAvatar(WATCHLIST_CREW_UID) || undefined;

  const loadRecent = useCallback(async () => {
    try {
      const [docs, pinCount] = await Promise.all([fetchCrewPostsForFeed(25), countActivePinsExcluding(null)]);
      setActivePinCount(pinCount);
      const now = Date.now();
      const rows: RecentCrewPostRow[] = docs.map(d => {
        const pinExp = timestampOrMillis(d.data, 'pinExpiresAt', 'pinExpiresAtMillis');
        const cam = d.data.createdAtMillis;
        const pem = d.data.pinExpiresAtMillis;
        return {
          id: d.id,
          title: typeof d.data.title === 'string' ? d.data.title : '(untitled)',
          authorUid: typeof d.data.authorUid === 'string' ? d.data.authorUid : '',
          createdAt: d.data.createdAt,
          createdAtMillis: typeof cam === 'number' ? cam : undefined,
          pinExpiresAt: d.data.pinExpiresAt,
          pinExpiresAtMillis: typeof pem === 'number' ? pem : undefined,
          isPinned: pinExp > now,
        };
      });
      rows.sort((a, b) => {
        const tb = timestampOrMillis(
          { createdAt: b.createdAt, createdAtMillis: b.createdAtMillis },
          'createdAt',
          'createdAtMillis'
        );
        const ta = timestampOrMillis(
          { createdAt: a.createdAt, createdAtMillis: a.createdAtMillis },
          'createdAt',
          'createdAtMillis'
        );
        return tb - ta;
      });
      setRecent(rows);
    } catch (e) {
      console.error('ModeratorConsole: load recent', e);
    }
  }, []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const draftLinks: CrewPostLinks = useMemo(
    () => ({
      watchlistIds: parseIdList(watchlistIds),
      movieIds: parseIdList(movieIds),
      reviewIds: parseIdList(reviewIds),
      userIds: parseIdList(userIds),
      personIds: parseIdList(personIds),
    }),
    [watchlistIds, movieIds, reviewIds, userIds, personIds]
  );

  const [previewSnapshotMs, setPreviewSnapshotMs] = useState(() => Date.now());
  useEffect(() => {
    if (previewOpen) setPreviewSnapshotMs(Date.now());
  }, [previewOpen]);

  const previewFeedItem = useMemo(() => {
    const t = title.trim();
    if (!t) return null;
    const base: Record<string, unknown> = {
      title: t,
      subtitle: subtitle.trim() || undefined,
      createdAt: previewSnapshotMs,
      links: draftLinks,
    };
    if (pinToTop) {
      base.pinnedAt = Timestamp.fromMillis(previewSnapshotMs);
      base.pinExpiresAt = Timestamp.fromMillis(previewSnapshotMs + composePinDays * MS_PER_DAY);
    }
    return crewPostDocToFeedItem('draft', base, previewSnapshotMs);
  }, [title, subtitle, draftLinks, pinToTop, composePinDays, previewSnapshotMs]);

  const composePinSummary = useMemo(() => {
    if (!pinToTop || !title.trim()) return null;
    const until = previewSnapshotMs + composePinDays * MS_PER_DAY;
    return `Preview: pinned to the top for ${composePinDays} day${composePinDays === 1 ? '' : 's'} (until ${new Date(until).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}). Up to ${MAX_PINNED_CREW_POSTS} posts can be pinned at once (${activePinCount} active now).`;
  }, [pinToTop, title, composePinDays, previewSnapshotMs, activePinCount]);

  const openPreview = () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setError(null);
    setPreviewOpen(true);
  };

  const executePublish = async () => {
    const t = title.trim();
    if (!t) {
      setError('Title is required');
      return;
    }
    if (pinToTop && activePinCount >= MAX_PINNED_CREW_POSTS) {
      setError(`At most ${MAX_PINNED_CREW_POSTS} posts can be pinned. Unpin one or wait for a pin to expire.`);
      return;
    }
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      await createCrewPost({
        authorUid: user.uid,
        title: t,
        subtitle: subtitle.trim() || undefined,
        links: draftLinks,
        pinDays: pinToTop ? composePinDays : undefined,
      });
      setSuccess('Post published. It will appear in the Crew feed as Watchlist Crew.');
      setPreviewOpen(false);
      setTitle('');
      setSubtitle('');
      setWatchlistIds('');
      setMovieIds('');
      setReviewIds('');
      setUserIds('');
      setPersonIds('');
      setPinToTop(false);
      setComposePinDays(7);
      await loadRecent();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to publish');
    } finally {
      setBusy(false);
    }
  };

  const openPinDialogForRow = (row: RecentCrewPostRow) => {
    setPinDialogDays(7);
    setPinDialogPost({ id: row.id, title: row.title, isPinned: row.isPinned });
  };

  const confirmApplyPin = async () => {
    if (!pinDialogPost) return;
    setBusy(true);
    setError(null);
    try {
      await updateCrewPostPin(pinDialogPost.id, pinDialogDays);
      setPinDialogPost(null);
      await loadRecent();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update pin');
    } finally {
      setBusy(false);
    }
  };

  const unpinRow = async (postId: string) => {
    setBusy(true);
    setError(null);
    try {
      await updateCrewPostPin(postId, null);
      await loadRecent();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to unpin');
    } finally {
      setBusy(false);
    }
  };

  const confirmRecall = async () => {
    if (!recallPost) return;
    if (!isAdmin && recallPost.authorUid !== user.uid) return;
    setBusy(true);
    setError(null);
    try {
      await deleteCrewPost(recallPost.id);
      setRecallPost(null);
      await loadRecent();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to recall post');
    } finally {
      setBusy(false);
    }
  };

  const canRecall = (authorUid: string) => isAdmin || authorUid === user.uid;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: isDark ? 'rgba(28, 28, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          pt: 'var(--safe-area-inset-top)',
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={onBack} sx={{ mr: 2 }} aria-label="Back">
            <ArrowBack />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontFamily: font(),
            }}
          >
            Moderator console
          </Typography>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 2,
            '& .MuiTab-root': { fontFamily: font(), textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab label="Crew posts" value={TAB_CREW_POSTS} />
          <Tab label="Reported comments" value={TAB_REPORTED_COMMENTS} />
          <Tab label="Reported posts & users" value={TAB_REPORTED_POSTS} />
        </Tabs>
      </AppBar>

      {activeTab === TAB_REPORTED_COMMENTS && (
        <ResponsiveContainer sx={{ py: 3, pb: 6 }}>
          <ReportedCommentsPanel />
        </ResponsiveContainer>
      )}

      {activeTab === TAB_REPORTED_POSTS && (
        <ResponsiveContainer sx={{ py: 3, pb: 6 }}>
          <ReportedPostsPanel />
        </ResponsiveContainer>
      )}

      {activeTab === TAB_CREW_POSTS && (
      <ResponsiveContainer sx={{ py: 3, pb: 6 }}>
        <Typography sx={{ fontFamily: font(), color: theme.palette.text.secondary, fontSize: 14, mb: 2 }}>
          Create official Crew feed posts. They appear under Watchlist Crew with optional links to watchlists,
          films, reviews, members, and cast or crew (TMDb person IDs).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          sx={{ mb: 2, fontFamily: font() }}
          required
        />
        <TextField
          fullWidth
          label="Subtitle (optional)"
          value={subtitle}
          onChange={e => setSubtitle(e.target.value)}
          sx={{ mb: 2, fontFamily: font() }}
          multiline
          minRows={2}
        />

        <Typography sx={{ fontFamily: font(), fontWeight: 600, mb: 1 }}>Deep links (IDs, comma or space separated)</Typography>

        <TextField
          fullWidth
          label="Watchlist IDs"
          value={watchlistIds}
          onChange={e => setWatchlistIds(e.target.value)}
          sx={{ mb: 1.5, fontFamily: font() }}
          placeholder="abc123, def456"
        />
        <TextField
          fullWidth
          label="Movie IDs"
          value={movieIds}
          onChange={e => setMovieIds(e.target.value)}
          sx={{ mb: 1.5, fontFamily: font() }}
          placeholder="550, tmdb_840430"
        />
        <TextField
          fullWidth
          label="Review document IDs"
          value={reviewIds}
          onChange={e => setReviewIds(e.target.value)}
          sx={{ mb: 1.5, fontFamily: font() }}
          placeholder="userMovies document id"
        />
        <TextField
          fullWidth
          label="User IDs (profiles)"
          value={userIds}
          onChange={e => setUserIds(e.target.value)}
          sx={{ mb: 1.5, fontFamily: font() }}
        />
        <TextField
          fullWidth
          label="TMDb person IDs (cast / crew)"
          value={personIds}
          onChange={e => setPersonIds(e.target.value)}
          sx={{ mb: 2, fontFamily: font() }}
          placeholder="287, 6193"
        />

        <Typography sx={{ fontFamily: font(), fontWeight: 600, mb: 1 }}>Pin to top (optional)</Typography>
        <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary', mb: 1 }}>
          Up to {MAX_PINNED_CREW_POSTS} official posts can be pinned at once. Pinned posts stay at the top of the Crew feed
          until they expire. Currently {activePinCount} active pin{activePinCount === 1 ? '' : 's'}.
        </Typography>
        {pinToTop && activePinCount >= MAX_PINNED_CREW_POSTS && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Pin limit reached. Unpin a post or wait for one to expire before publishing a new pinned post.
          </Alert>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={pinToTop}
              onChange={(_, v) => setPinToTop(v)}
              disabled={busy}
              color="primary"
            />
          }
          label="Pin this post when published"
          sx={{ fontFamily: font(), display: 'block', mb: pinToTop ? 1 : 2 }}
        />
        {pinToTop && (
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel id="moderator-compose-pin-days">Pin duration</InputLabel>
            <Select
              labelId="moderator-compose-pin-days"
              label="Pin duration"
              value={composePinDays}
              onChange={e => setComposePinDays(Number(e.target.value))}
              disabled={busy}
              sx={{ fontFamily: font() }}
            >
              {PIN_DURATION_DAYS.map(d => (
                <MenuItem key={d} value={d}>
                  {d} day{d === 1 ? '' : 's'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button
          variant="contained"
          onClick={openPreview}
          disabled={busy || !title.trim()}
          sx={{ fontFamily: font(), textTransform: 'none', mb: 3 }}
        >
          Preview
        </Button>

        <AdminPublishPreviewShell
          open={previewOpen}
          onClose={() => !busy && setPreviewOpen(false)}
          title="Preview Crew post"
          onPublish={executePublish}
          publishing={busy}
          publishLabel="Publish"
          publishDisabled={pinToTop && activePinCount >= MAX_PINNED_CREW_POSTS}
        >
          {previewFeedItem ? (
            <CrewPostComposePreviewPanel
              item={previewFeedItem}
              actorName={crewDisplayName}
              actorAvatar={crewAvatar}
              currentUser={user}
              pinSummary={composePinSummary}
            />
          ) : (
            <Typography sx={{ fontFamily: font(), color: 'text.secondary' }}>Enter a title to see a preview.</Typography>
          )}
        </AdminPublishPreviewShell>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontFamily: font(), fontWeight: 600, mb: 1 }}>
          Published posts (newest first)
        </Typography>
        <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
          Pin keeps a post at the top until it expires (max {MAX_PINNED_CREW_POSTS} pins). Recall removes a post from
          everyone&apos;s Crew feed. You can recall your own posts; admins can recall any post. Use{' '}
          <strong>Copy post ID</strong> for custom notifications (Admin → Send notification → Crew post document ID).
        </Typography>
        <List dense disablePadding>
          {recent.map(row => (
            <ListItem
              key={row.id}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <ListItemText
                sx={{ flex: '1 1 200px', minWidth: 0 }}
                primary={
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                    <Typography component="span" sx={{ fontFamily: font(), fontSize: 14 }}>
                      {row.title}
                    </Typography>
                    {row.isPinned && (
                      <Chip
                        size="small"
                        color="warning"
                        label={`Pinned until ${formatFirebaseDate(row.pinExpiresAtMillis ?? row.pinExpiresAt, '—', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}`}
                        sx={{ fontFamily: font(), fontSize: 11 }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box component="span" sx={{ display: 'block' }}>
                    <Typography component="span" sx={{ fontFamily: font(), fontSize: 12, display: 'block' }}>
                      {formatFirebaseDate(row.createdAtMillis ?? row.createdAt, '—', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25,
                        flexWrap: 'wrap',
                        mt: 0.75,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'ui-monospace, monospace',
                          fontSize: 11,
                          color: 'text.secondary',
                          wordBreak: 'break-all',
                        }}
                      >
                        {row.id}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Copy post ID for notifications"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(row.id);
                            setSuccess('Post ID copied — paste it in Admin → Send notification → Crew post document ID.');
                            window.setTimeout(() => setSuccess(null), 4000);
                          } catch {
                            setError('Could not copy to clipboard.');
                          }
                        }}
                        edge="end"
                      >
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                }
                primaryTypographyProps={{ fontFamily: font(), fontSize: 14 }}
                secondaryTypographyProps={{ component: 'div' }}
              />
              <Stack
                direction="row"
                flexWrap="wrap"
                spacing={0.5}
                useFlexGap
                sx={{ justifyContent: 'flex-end', flex: '0 1 auto', py: 0.25 }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openPinDialogForRow(row)}
                  disabled={busy}
                  sx={{ fontFamily: font(), textTransform: 'none', fontSize: 12 }}
                >
                  {row.isPinned ? 'Pin duration…' : 'Pin…'}
                </Button>
                {row.isPinned && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => unpinRow(row.id)}
                    disabled={busy}
                    sx={{ fontFamily: font(), textTransform: 'none', fontSize: 12 }}
                  >
                    Unpin
                  </Button>
                )}
                {canRecall(row.authorUid) && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => setRecallPost({ id: row.id, title: row.title, authorUid: row.authorUid })}
                    disabled={busy}
                    sx={{ fontFamily: font(), textTransform: 'none', fontSize: 12 }}
                  >
                    Recall
                  </Button>
                )}
              </Stack>
            </ListItem>
          ))}
        </List>
      </ResponsiveContainer>
      )}

      <Dialog open={!!pinDialogPost} onClose={() => !busy && setPinDialogPost(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: font(), fontWeight: 700, pr: 5 }}>
          {pinDialogPost?.isPinned ? 'Update pin duration' : 'Pin to top'}
          <IconButton
            aria-label="Close"
            onClick={() => !busy && setPinDialogPost(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: font(), fontSize: 14, color: 'text.primary', mb: 2 }}>
            {pinDialogPost?.isPinned ? 'Set a new expiry from now.' : 'Choose how long to keep this post pinned.'}{' '}
            &ldquo;{pinDialogPost?.title}&rdquo;
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="moderator-pin-dialog-days">Duration</InputLabel>
            <Select
              labelId="moderator-pin-dialog-days"
              label="Duration"
              value={pinDialogDays}
              onChange={e => setPinDialogDays(Number(e.target.value))}
              disabled={busy}
              sx={{ fontFamily: font() }}
            >
              {PIN_DURATION_DAYS.map(d => (
                <MenuItem key={d} value={d}>
                  {d} day{d === 1 ? '' : 's'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setPinDialogPost(null)} disabled={busy} sx={{ fontFamily: font(), textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={confirmApplyPin} variant="contained" disabled={busy} sx={{ fontFamily: font(), textTransform: 'none' }}>
            {busy ? 'Saving…' : pinDialogPost?.isPinned ? 'Update' : 'Pin'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!recallPost} onClose={() => !busy && setRecallPost(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: font(), fontWeight: 700, pr: 5 }}>
          Recall post?
          <IconButton
            aria-label="Close"
            onClick={() => !busy && setRecallPost(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: font(), fontSize: 14, color: 'text.primary' }}>
            &ldquo;{recallPost?.title}&rdquo; will be removed from the Crew feed for all users. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setRecallPost(null)} disabled={busy} sx={{ fontFamily: font(), textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={confirmRecall} color="error" variant="contained" disabled={busy} sx={{ fontFamily: font(), textTransform: 'none' }}>
            {busy ? 'Removing…' : 'Recall'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModeratorConsole;
